import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  subCategories: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const subCategorySlice = createSlice({
  name: 'subCategory',
  initialState,
  reducers: {
    setSubCategories: (state, action) => {
      state.subCategories = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setSubCategories, setTotalCount, setTotalPages, setLoading, setError } =
  subCategorySlice.actions;

// Get sub categories list (paginated & searchable)
export const getSubCategories = (filters = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const categoryId = filters.categoryId || '';

    const params = { page, limit, search };
    if (categoryId) params.category_id = categoryId;

    const response = await axiosInstance.get('/admin/sub-category/list', { params });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || response.data.data || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setSubCategories(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setSubCategories([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch sub categories';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setSubCategories([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch sub categories';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single sub category details
export const getSubCategoryDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/sub-category/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch sub category details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch sub category details';
    return { status: false, message: errorMsg };
  }
};

// Add new sub category
export const addSubCategory = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/sub-category/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add sub category' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add sub category';
    return { status: false, message: errorMsg };
  }
};

// Update sub category
export const updateSubCategory = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/sub-category/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update sub category' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update sub category';
    return { status: false, message: errorMsg };
  }
};

// Delete sub category
export const deleteSubCategory = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/sub-category/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete sub category' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete sub category';
    return { status: false, message: errorMsg };
  }
};

export default subCategorySlice.reducer;
